import { useState } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  CloseButton,
  Flex,
  useTheme,
  IconButton,
} from '@chakra-ui/react';
import ChatContainer from './ChatContainer';
import Sidebar from './Sidebar/Sidebar';
import Message from './Message';
import SidebarButton from './Sidebar/SidebarButton';
import MessageFeedbackModal from './MessageFeedbackModal';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCode,
  faCompressAlt,
  faExpandAlt,
} from '@fortawesome/free-solid-svg-icons';
import LandingPage from './LandingPage';
import { v4 as uuidv4 } from 'uuid';
import { CONVERSATION_TYPES, USER_FEEDBACK_TYPES } from '../../config';

function ProviderChat({
  activeConversationId,
  setActiveConversationId,
  conversations,
  setConversations,
  messages,
  setMessages,
  latestSearchResults,
  setLatestSearchResults,
  searchParameters,
  setSearchParameters,
  totalSearchResultsCount,
  setTotalSearchResultsCount,
  latestServerMessage,
  sendUserMessageToServer,
  isSidebarOpen,
  setIsSidebarOpen,
  expandedView,
  toggleExpandedView,
  devMode,
  toggleDevMode,
}) {
  const theme = useTheme();
  const { user } = useAuth0();
  const [botStatus, setBotStatus] = useState({
    progress: 0,
    text: '',
  });
  const [isTyping, setIsTyping] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [
    showPositiveMessageFeedbackModal,
    setShowPositiveMessageFeedbackModal,
  ] = useState(false);
  const [
    showNegativeMessageFeedbackModal,
    setShowNegativeMessageFeedbackModal,
  ] = useState(false);
  const [messageSelectedForFeedback, setMessageSelectedForFeedback] =
    useState(null);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
  const [showProviderLoadingScreen, setShowProviderLoadingScreen] =
    useState(false);

  // Ivan says keep an eye on this code
  useEffect(() => {
    // If the server sends the "Endpoint request timed out" message, ignore it
    if (
      (!latestServerMessage.stopTyping &&
        !latestServerMessage.streamFinished &&
        latestServerMessage.streaming) ||
      (latestServerMessage.stopTyping &&
        latestServerMessage.streamFinished &&
        latestServerMessage.streaming)
    ) {
      setShowLoadingAnimation(false);
    } else {
      setShowLoadingAnimation(true);
    }

    if (latestServerMessage.message === 'Endpoint request timed out') {
      return;
    }

    if (latestServerMessage?.statusCode === 500) {
      setErrorMessage('Error Occurred, please try again');
      setShowErrorAlert(true);
      setIsTyping(() => false);
      setBotStatus({ progress: 0, text: 'Error Occurred, please try again' });
      return;
    }

    if (latestServerMessage?.statusCode === 413) {
      console.debug('message too big!');
      // delete the large invalid message from list of messages
      const updatedMessages = messages.slice(0, -1);
      // tell the user that the message is too large
      setErrorMessage(
        'That message was too large. Please try again with a shorter message.',
      );
      setShowErrorAlert(true);
      setIsTyping(() => false);
      setBotStatus({ progress: 0, text: '' });
      setMessages(updatedMessages);
      return;
    }

    if (latestServerMessage.stopTyping) {
      if (messages.length > 0) {
        const updatedMessages = messages.slice(0, -1);
        let lastMessage = {
          ...messages[messages.length - 1],
          streaming: false,
          conversationId: activeConversationId,
        };
        if (latestServerMessage.messageDynamoRecord) {
          lastMessage = {
            ...lastMessage,
            ...latestServerMessage.messageDynamoRecord,
          };
        }
        updatedMessages.push(lastMessage);
        setMessages(updatedMessages);
      }
      setIsTyping(() => false);
      setBotStatus({ progress: 0, text: '' });
      return;
    }
    // Handle streaming messages separately
    if (latestServerMessage.streaming) {
      const lastMessage = messages[messages.length - 1];

      // If it's the start of a new stream (or the messages array is empty)
      if (
        !lastMessage ||
        lastMessage.streamFinished ||
        !lastMessage.streaming
      ) {
        setMessages([...messages, latestServerMessage]);
      } else {
        // Update the current streaming message
        const updatedMessages = messages.slice(0, -1); // Excluding the last message
        const updatedMessage = {
          ...lastMessage,
          text: lastMessage.text + latestServerMessage.text,
          streamFinished: latestServerMessage.streamFinished,
        };
        if (!latestServerMessage.streamFinished) {
          const isNewConversation = () =>
            conversations.find(
              (conversation) =>
                conversation.conversationId === activeConversationId,
            )?.isNewConversation === true;
          if (isNewConversation) {
            const updatedConversations = conversations.map((conversation) => {
              if (conversation.conversationId === activeConversationId) {
                return {
                  ...conversation,
                  isNewConversation: false,
                };
              }
              return conversation;
            });
            setConversations(updatedConversations);
          }
          setMessages([...updatedMessages, updatedMessage]);
        }
      }

      return; // Exit early since streaming logic is handled
    }

    //Push server messages to array
    if (latestServerMessage.sender) {
      //Check for message validity; avoid initial {}
      if (latestServerMessage.status) {
        //Status progress updates
        setBotStatus(latestServerMessage);
      } else {
        if (
          devMode ||
          latestServerMessage.stopTyping ||
          latestServerMessage.relevantSearchParameters
        ) {
          //Messages with details about SQL
          setMessages([...messages, latestServerMessage]);
        }
      }
    }

    if (
      latestServerMessage.text?.includes(
        'Finding provider based on search criteria...',
      )
    ) {
      setShowProviderLoadingScreen(true);
    }

    //Set the latest search results to display in the card
    if (
      latestServerMessage.text?.includes('Retrieved provider search results')
    ) {
      const responseData = JSON.parse(latestServerMessage.data);
      setLatestSearchResults(responseData.searchResults);
      setTotalSearchResultsCount(responseData.totalResultsCount);
    }

    //Set search parameters to fill See Why functionality
    if (latestServerMessage.dataType === 'relevantSearchParameters') {
      setSearchParameters(
        JSON.parse(latestServerMessage.relevantSearchParameters),
      );
    }
  }, [latestServerMessage]);

  const openPositiveMessageFeedbackModal = (message) => {
    setMessageSelectedForFeedback(message);
    setShowPositiveMessageFeedbackModal(true);
  };

  const openNegativeMessageFeedbackModal = (message) => {
    setMessageSelectedForFeedback(message);
    setShowNegativeMessageFeedbackModal(true);
  };

  const closePositiveMessageFeedbackModal = () => {
    setShowPositiveMessageFeedbackModal(false);
    setMessageSelectedForFeedback(null);
  };

  const closeNegativeMessageFeedbackModal = () => {
    setShowNegativeMessageFeedbackModal(false);
    setMessageSelectedForFeedback(null);
  };

  const handleMessageFeedbackSubmission = (feedbackMessage, feedbackType) => {
    const { PK, SK } = feedbackMessage;
    setMessages(
      messages.map((message) => {
        if (message.PK === PK && message.SK === SK) {
          return { ...message, feedbackType };
        }
        return message;
      }),
    );
  };

  const handleSend = async (text) => {
    let conversationId = activeConversationId;
    if (!conversationId) {
      conversationId = uuidv4();
      const newConversation = {
        conversationType: CONVERSATION_TYPES.PROVIDER,
        conversationId,
        conversationTitle: text,
        isNewConversation: true,
      };
      console.debug('newConversation: ', newConversation);
      setActiveConversationId(conversationId);
      setConversations([newConversation, ...conversations]);
    }
    const newMessage = {
      text,
      sender: 'user',
    };
    const newMessages = [...messages, newMessage];
    setShowLoadingAnimation(true);
    setMessages(newMessages);
    setShowErrorAlert(false);
    setErrorMessage('');
    setBotStatus({
      progress: 5,
      text: 'Taking a look!',
    });
    setIsTyping(true);
    sendUserMessageToServer(newMessage, conversationId);
  };

  return (
    <Flex direction='row'>
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        conversations={conversations}
        activeConversationId={activeConversationId}
        setActiveConversationId={setActiveConversationId}
        setShowLoadingAnimation={setShowLoadingAnimation}
        setShowProviderLoadingScreen={setShowProviderLoadingScreen}
        setMessages={setMessages}
        expandedView={expandedView}
        isDisabled={isTyping}
        setSearchResults={setLatestSearchResults}
      />
      {isSidebarOpen ? null : (
        <Box ml={2.5} mt={expandedView ? '0px' : '40px'}>
          <SidebarButton onClose={() => setIsSidebarOpen(!isSidebarOpen)} />
        </Box>
      )}
      <Flex
        justifyContent='space-between'
        direction='column'
        h={
          expandedView
            ? 'calc(100vh - 50px)'
            : 'calc(100vh - 64px - 47px - 60px)'
        } //Remove the header height, footer height, and the margin
        m={expandedView ? '0px auto' : '30px auto'}
        borderRadius='5px'
        // gap="10px"
        width='90%'
        position='relative'
        boxShadow={theme.shadow?.md}
      >
        <IconButton
          position='absolute'
          top='0px'
          right='0px'
          opacity={0.3}
          // colorScheme="whiteAlpha"
          backgroundColor={theme.colors.transparent}
          _hover={{
            backgroundColor: theme.colors.transparent,
            opacity: 1,
          }}
          _active={{
            backgroundColor: theme.colors.transparent,
            opacity: 1,
          }}
          icon={
            expandedView ? (
              <FontAwesomeIcon icon={faCompressAlt} />
            ) : (
              <FontAwesomeIcon icon={faExpandAlt} />
            )
          }
          onClick={toggleExpandedView}
          aria-label='Expand chat view'
        />
        <IconButton
          position='absolute'
          top='0px'
          right='-50px'
          opacity={devMode ? 1 : 0.3}
          // colorScheme="whiteAlpha"
          backgroundColor={
            devMode ? theme.colors.green : theme.colors.transparent
          }
          _hover={{
            backgroundColor: devMode
              ? theme.colors.green
              : theme.colors.transparent,
            opacity: 1,
          }}
          _active={{
            backgroundColor: devMode
              ? theme.colors.green
              : theme.colors.transparent,
            opacity: 1,
          }}
          icon={<FontAwesomeIcon icon={faCode} />}
          onClick={toggleDevMode}
          aria-label='Toggle Dev Mode'
        />
        {!activeConversationId ? (
          <LandingPage
            handleSend={handleSend}
            setShowProviderLoadingScreen={setShowProviderLoadingScreen}
            searchResults={latestSearchResults}
            setSearchResults={setLatestSearchResults}
          />
        ) : (
          <>
            <ChatContainer
              messages={messages}
              messageInputProps={{
                expandedView: expandedView,
                forceDisable: isTyping,
                sendMessage: handleSend,
              }}
              activeConversationId={activeConversationId}
              searchResults={latestSearchResults}
              searchParameters={searchParameters}
              setSearchResults={setLatestSearchResults}
              totalSearchResultsCount={totalSearchResultsCount}
              showLoadingAnimation={showLoadingAnimation}
              setShowLoadingAnimation={setShowLoadingAnimation}
              showProviderLoadingScreen={showProviderLoadingScreen}
              setShowProviderLoadingScreen={setShowProviderLoadingScreen}
              openPositiveFeedbackModal={openPositiveMessageFeedbackModal}
              openNegativeFeedbackModal={openNegativeMessageFeedbackModal}
            />
            {showErrorAlert && (
              <Box width='80%' mx='auto'>
                <Alert
                  status='error'
                  borderRadius='5px'
                  m='10px'
                  bgColor='#FFD2D2'
                >
                  <AlertIcon color='#E53E3E' />
                  <AlertTitle color='#E53E3E' mr={2}>
                    {errorMessage}
                  </AlertTitle>
                  <CloseButton
                    position='absolute'
                    right='8px'
                    top='8px'
                    onClick={() => {
                      setShowErrorAlert(false);
                      setErrorMessage('');
                    }}
                  />
                </Alert>
              </Box>
            )}
            {isTyping && devMode && (
              <Box
                ml='30px'
                mb='5px'
                color={theme.colors.mediumGrey3}
                fontSize='14px'
              >
                Cara is typing...
              </Box>
            )}
          </>
        )}
      </Flex>
      <MessageFeedbackModal
        message={messageSelectedForFeedback}
        isOpen={showPositiveMessageFeedbackModal}
        onClose={closePositiveMessageFeedbackModal}
        feedbackType={USER_FEEDBACK_TYPES.POSITIVE}
        onFeedbackSubmitted={handleMessageFeedbackSubmission}
      />
      <MessageFeedbackModal
        message={messageSelectedForFeedback}
        isOpen={showNegativeMessageFeedbackModal}
        onClose={closeNegativeMessageFeedbackModal}
        feedbackType={USER_FEEDBACK_TYPES.NEGATIVE}
        onFeedbackSubmitted={handleMessageFeedbackSubmission}
      />
    </Flex>
  );
}

export default ProviderChat;
